import { useRef } from "react";
import CountUp from "react-countup";
import { motion, useInView } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { FLAGSHIPS, type Flagship } from "@/lib/brand";
import { trackProjectClick } from "@/lib/analytics";
import { fadeUp, staggerContainer } from "@/lib/animations";

/**
 * Trust strip — static project tile grid + stats row.
 *
 * The previous version used an auto-scrolling text marquee + a separate
 * stats grid. Replaced with a deliberate static 4×2 grid of project tiles
 * (no motion) so the section reads as a confident showcase rather than
 * kinetic decoration. The component still owns the headline stats counter
 * (102+ clients, 45+ projects, 7+ years, 99% satisfaction).
 *
 * This IS the project showcase on the homepage — each tile shows the
 * project name (Latin + Arabic), a numbered tag, and a brand-color stripe
 * that grows on hover, and links out to the live project. There is no
 * separate flagships section below; deeper details live at each
 * project's own URL.
 */

const FLAGSHIP_HUE: Record<string, number> = {
    kasioon: 280,
    "carwhats-group": 220,
    carwhats: 160,
    "megaquantum-nl": 245,
    "megaquantum-net": 300,
    vivaverse: 335,
    sport24seven: 35,
    "oscar-news": 200,
};

interface TickerStat {
    end: number;
    suffix: string;
    labelKey: string;
}

const STATS: TickerStat[] = [
    { end: 102, suffix: "+", labelKey: "trust_marquee.label_clients" },
    { end: 45, suffix: "+", labelKey: "trust_marquee.label_projects" },
    { end: 7, suffix: "+", labelKey: "trust_marquee.label_years" },
    { end: 99, suffix: "%", labelKey: "trust_marquee.label_satisfaction" },
];

const ProjectTile = ({ flagship, index }: { flagship: Flagship; index: number }) => {
    const num = String(index + 1).padStart(2, "0");
    const hue = FLAGSHIP_HUE[flagship.key] ?? 260;
    const accent = `hsl(${hue} 90% 60%)`;

    return (
        <motion.a
            variants={fadeUp}
            href={flagship.url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackProjectClick(flagship.name, flagship.url, "home_trust_grid")}
            data-cursor="hover"
            className="group relative bg-background hover:bg-white/[0.025] transition-colors duration-300 p-5 sm:p-6 flex flex-col justify-between min-h-[160px] sm:min-h-[180px]"
        >
            {/* Top edge neon stroke — animates in on hover */}
            <span
                aria-hidden
                className="absolute top-0 inset-x-0 h-px scale-x-0 group-hover:scale-x-100 origin-start transition-transform duration-500"
                style={{ background: "var(--gradient-accent-v2)" }}
            />

            {/* Top row — numbered tag + outbound arrow */}
            <div className="flex items-start justify-between">
                <span className="font-mono-brutal text-[10px] text-white/45 tabular-nums">
                    [{num}]
                </span>
                <ArrowUpRight
                    size={14}
                    className="text-white/35 transition-all duration-300 group-hover:text-white group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                />
            </div>

            {/* Project identity stack */}
            <div>
                <h3 className="font-v2-display text-lg sm:text-xl md:text-2xl text-white leading-tight">
                    {flagship.name}
                </h3>
                <p
                    className="font-v2-display text-sm text-white/55 mt-1"
                    dir="rtl"
                    lang="ar"
                >
                    {flagship.nameAr}
                </p>

                {/* Brand-color underline — short by default, fills on hover */}
                <span
                    aria-hidden
                    className="block mt-3 sm:mt-4 h-[2px] w-6 group-hover:w-full transition-[width] duration-500 ease-out"
                    style={{
                        background: `linear-gradient(to right, ${accent}, transparent)`,
                    }}
                />
            </div>
        </motion.a>
    );
};

const TrustMarqueeStrip = () => {
    const { t } = useTranslation();
    const sectionRef = useRef<HTMLDivElement>(null);
    const inView = useInView(sectionRef, { once: true, margin: "-80px" });

    return (
        <section
            ref={sectionRef}
            id="trust"
            aria-label={t("trust_marquee.eyebrow")}
            className="py-12 sm:py-16 md:py-24"
        >
            <div className="container mx-auto px-5 sm:px-6">
                {/* Eyebrow header */}
                <div className="flex items-center gap-3 mb-8 sm:mb-10">
                    <span aria-hidden className="brutal-glyph" style={{ height: "0.7em" }} />
                    <span className="font-mono-brutal text-[10px] sm:text-xs text-white/55">
                        {t("trust_marquee.eyebrow")}
                    </span>
                    <span aria-hidden className="flex-1 h-px bg-white/10" />
                    <span className="hidden sm:inline font-mono-brutal text-[10px] text-white/35 tabular-nums">
                        [{String(FLAGSHIPS.length).padStart(2, "0")} PROJECTS]
                    </span>
                </div>

                {/* Project tile grid — 2 cols mobile, 4 cols desktop. The
                    `gap-px bg-white/10` trick draws hairline separators
                    between tiles without each tile needing its own border. */}
                <motion.div
                    variants={staggerContainer(0.05, 0.05)}
                    initial="hidden"
                    whileInView="show"
                    viewport={{ once: true, amount: 0.1 }}
                    className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 mb-10 sm:mb-12"
                >
                    {FLAGSHIPS.map((flagship, i) => (
                        <ProjectTile key={flagship.key} flagship={flagship} index={i} />
                    ))}
                </motion.div>

                {/* Stats row */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10">
                    {STATS.map((stat) => (
                        <div
                            key={stat.labelKey}
                            className="bg-background flex flex-col items-start justify-center gap-1 px-4 sm:px-6 py-5 sm:py-6"
                        >
                            <span className="font-mono-brutal text-[10px] text-white/45">
                                {t(stat.labelKey)}
                            </span>
                            <p className="font-v2-display text-3xl sm:text-4xl md:text-5xl text-white leading-none tabular-nums">
                                {inView ? (
                                    <CountUp end={stat.end} duration={1.6} useEasing suffix={stat.suffix} />
                                ) : (
                                    <span>0{stat.suffix}</span>
                                )}
                            </p>
                        </div>
                    ))}
                </div>

                <p className="mt-5 sm:mt-6 font-mono-brutal text-[10px] sm:text-xs text-white/40 text-center sm:text-start">
                    {t("trust_marquee.footnote")}
                </p>
            </div>
        </section>
    );
};

export default TrustMarqueeStrip;
